import { generateGUID } from '@wix/cashier-common/dist/src/utils/guid/generateGUID';
import React from 'react';
import { Theme } from '../../../../types/Theme';
import { t } from '../../../../utils/i18n';
import { DropdownOption } from '../../../Dropdown';
import { BaseFieldProps } from '../types';
import { TextField } from '../TextField';
import { DropdownFieldAPI, DropdownFieldItem } from './types';
import { mapItemsToOptionsFormat } from './utils';
import { DropdownFieldView } from './view';

interface DropdownFieldProps extends BaseFieldProps {
  withInputFallback?: boolean;

  fetchItems(): Promise<DropdownFieldItem[]>;
  onApiInit?(api: DropdownFieldAPI): void;
}

interface DropdownFieldState {
  isLoadingOptions: boolean;
  isLoadingOptionsFailed: boolean;
  items: DropdownOption[];
}

export class DropdownField extends React.Component<
  DropdownFieldProps,
  DropdownFieldState
> {
  static cachedItems: { [key: string]: DropdownFieldItem[] } = {};
  static defaultProps: Partial<DropdownFieldProps> = {
    deviceType: 'desktop',
    theme: Theme.default,
  };
  isComponentMounted: boolean = false;
  activeFetchItemsRequestId: string = null;

  constructor(props: DropdownFieldProps) {
    super(props);

    this.state = {
      isLoadingOptions: false,
      isLoadingOptionsFailed: false,
      items: mapItemsToOptionsFormat(this.cachedItems),
    };
  }

  componentDidMount() {
    this.isComponentMounted = true;

    if (this.cachedItems.length === 0) {
      this.fetchItems();
    }

    if (this.props.onApiInit) {
      this.props.onApiInit({
        reloadItems: () => this.fetchItems(),
      });
    }
  }

  componentWillUnmount() {
    this.isComponentMounted = false;
    DropdownField.cachedItems[this.props.name] = [];
  }

  safeSetState = (state, cb?) => {
    if (this.isComponentMounted) {
      this.setState(state, cb);
    } else if (cb) {
      cb();
    }
  };

  get cachedItems(): DropdownFieldItem[] {
    return DropdownField.cachedItems[this.props.name] || [];
  }

  async fetchItems() {
    const requestId: string = generateGUID();

    this.activeFetchItemsRequestId = requestId;
    this.setState({ isLoadingOptions: true, isLoadingOptionsFailed: false });

    let rawItems: DropdownFieldItem[];

    try {
      rawItems = await this.props.fetchItems();

      if (requestId !== this.activeFetchItemsRequestId) {
        return;
      }
    } catch (e) {
      if (requestId !== this.activeFetchItemsRequestId) {
        return;
      }
      this.safeSetState({
        isLoadingOptionsFailed: true,
      });
      rawItems = null;
    }

    if (rawItems !== null) {
      DropdownField.cachedItems[this.props.name] = rawItems;
      this.safeSetState({ items: mapItemsToOptionsFormat(rawItems) });
    }

    this.safeSetState({ isLoadingOptions: false });
    this.activeFetchItemsRequestId = null;
  }

  handleSelect = (option: DropdownOption) => {
    this.props.onChange({
      value: option.id as string,
      name: this.props.name,
      isValid: this.props.validate(option.id as string),
    });
  };

  render() {
    const {
      required,
      value,
      name,
      label,
      deviceType,
      shouldApplySiteStyles,
      errorMessage,
      withInputFallback,
      validate,
      theme,
      locale,
    } = this.props;

    const { isLoadingOptions, isLoadingOptionsFailed, items } = this.state;
    const labelText = t(label);

    if (withInputFallback && (isLoadingOptionsFailed || items?.length === 0)) {
      return (
        <TextField
          label={labelText}
          name={name}
          value={value}
          theme={theme}
          required={required}
          locale={locale}
          shouldApplySiteStyles={this.props.shouldApplySiteStyles}
          deviceType={this.props.deviceType}
          errorMessage={errorMessage}
          isValid={this.props.isValid}
          touched={this.props.touched}
          onChange={this.props.onChange}
          onBlur={() => {}}
          validate={validate}
        />
      );
    }

    const showLabel = theme !== Theme.modal || deviceType === 'desktop';

    return (
      <DropdownFieldView
        deviceType={deviceType}
        isValidationError={this.props.touched && !this.props.isValid}
        shouldApplySiteStyles={shouldApplySiteStyles}
        labelText={labelText}
        name={name}
        required={required}
        showLabel={showLabel}
        theme={theme}
        value={value}
        errorMessage={errorMessage}
        isLoadingOptions={isLoadingOptions}
        items={items}
        onSelect={this.handleSelect}
      />
    );
  }
}
